import { logisticsApi } from "./ApiClient";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { generateQueryString } from "./Helpers";
import type {
  IConvertedPatientNoteWithPatientId,
  ICreatePatientNote,
  IPatientNoteStatus,
} from "@medoma/models/patientNotes";
import {
  convertedPatientNoteSchema,
  patientNoteStatusSchema,
} from "@medoma/models/patientNotes";

export async function convertPatientNote(patientNote: ICreatePatientNote) {
  await logisticsApi.post(`/conversion/patient-note`, {
    json: { ...patientNote },
  });
}

const acceptedConvertedPatientNoteSchema = z.object({
  id: z.uuid(),
  updatedText: z.string(),
});

export type IAcceptedConvertedPatientNote = z.infer<
  typeof acceptedConvertedPatientNoteSchema
>;

export async function acceptConvertedPatientNote(
  convertedPatientNote: IAcceptedConvertedPatientNote & {
    status: IPatientNoteStatus;
  },
) {
  const shouldUpdate =
    convertedPatientNote.status === patientNoteStatusSchema.enum.accepted;

  if (shouldUpdate) {
    await logisticsApi.put(
      `/conversion/patient-note/${convertedPatientNote.id}/accept`,
      {
        json: { updatedText: convertedPatientNote.updatedText },
      },
    );
  } else {
    await logisticsApi.post(
      `/conversion/patient-note/${convertedPatientNote.id}/accept`,
      {
        json: { updatedText: convertedPatientNote.updatedText },
      },
    );
  }
}

const fetchPatientNoteById = async (
  patientId: string,
  status: IPatientNoteStatus,
): Promise<IConvertedPatientNoteWithPatientId | null> => {
  const queryString = generateQueryString({
    status: status,
  });

  let response;

  try {
    response = await logisticsApi
      .get(`/conversion/patient-note/latest/patient/${patientId}${queryString}`)
      .json(convertedPatientNoteSchema);
  } catch {
    return null;
  }

  return {
    patientId,
    ...response,
  };
};

const patientNoteKeys = {
  all: ["conversion"] as const,
  lists: () => [...patientNoteKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) =>
    [...patientNoteKeys.lists(), filters] as const,
  detail: (patientId: string, status: IPatientNoteStatus) =>
    [...patientNoteKeys.all, patientId, "details", status] as const,
};

export const usePatientNote = (
  patientId: string,
  status: IPatientNoteStatus,
) => {
  return useQuery({
    queryKey: patientNoteKeys.detail(patientId, status),
    queryFn: () => fetchPatientNoteById(patientId, status),
  });
};
